import axios from 'axios';

import { EApiBaseCode } from '@/constants';
import { DEFAULT_BACKEND_SWIMLANE } from '@/constants/fetch';
import { STORAGE_KEY_BACKEND_SWIMLANE } from '@/constants/storage';
import { isProdEnv } from '@/utils/env';
import { localStore } from '@/utils/storage';
import { logPageDataLoadedTrack } from '@/utils/tracker/global-info';

import { buildH5CommonParams } from './build-common-params';
import {
    beginAuthenticatedRequest,
    getSessionRequestWait,
    getSessionVersion,
    recoverSessionFromAuthError,
} from './session-gate';
import { formatRequestOptions, formatRequestParams, handleFetchFailed, handleFetchSuccess } from './tools';

import type {
    IFetchConfig,
    IGetDataResponseConfig,
    IGetOriginResponseConfig,
    IRequestOptions,
    IResponse,
    TOriginResponse,
} from './typings';

import type { AxiosRequestConfig } from 'axios';

let hasFirstValidRequest = false;

function throwIfRequestCanceled(config: AxiosRequestConfig): void {
    config.cancelToken?.throwIfRequested();
    if (config.signal?.aborted) {
        throw new axios.CanceledError('canceled');
    }
}

/** 取消只结束该业务请求的排队，不取消其他请求共享的会话 login。 */
async function waitForSessionTask(task: Promise<void>, config: AxiosRequestConfig): Promise<void> {
    const { signal } = config;
    throwIfRequestCanceled(config);
    if (!signal) {
        await task;
        throwIfRequestCanceled(config);
        return;
    }
    let onAbort: () => void;
    try {
        await Promise.race([
            task,
            new Promise<never>((_, reject) => {
                onAbort = () => reject(new axios.CanceledError('canceled'));
                signal.addEventListener?.('abort', onAbort);
            }),
        ]);
        throwIfRequestCanceled(config);
    } finally {
        signal.removeEventListener?.('abort', onAbort!);
    }
}

function isBusinessAuthError(error: unknown): boolean {
    // fetch 主动抛出的 2xx 原响应与 Axios 非 2xx 错误分别取其业务包体。
    if (error === null || typeof error !== 'object') {
        return false;
    }
    const data = axios.isAxiosError<IResponse<unknown>>(error)
        ? error.response?.data
        : (error as TOriginResponse<unknown>).data;
    return data?.code === EApiBaseCode.TOKEN_MISSING || data?.code === EApiBaseCode.TOKEN_INVALID;
}

// fetch 方法，会调用 axios 发起请求，并处理返回信息
export async function fetch<RequestData, ResponseData>(
    config: IFetchConfig<RequestData>
): Promise<IResponse<ResponseData> | TOriginResponse<ResponseData>> {
    const { method, url, data: configData } = config;
    const requiresAuth = config.requiresAuth ?? false;
    const formatOptions = formatRequestOptions(config);
    const formatParams = formatRequestParams<RequestData>({ options: formatOptions, method, data: configData });
    const { axiosConfig, isNeedOriginRes } = formatOptions;
    throwIfRequestCanceled(axiosConfig);
    let sessionWait = getSessionRequestWait(requiresAuth);
    while (sessionWait) {
        await waitForSessionTask(sessionWait, axiosConfig);
        sessionWait = getSessionRequestWait(requiresAuth);
    }
    const commonParams = {
        ...(await buildH5CommonParams()),
        ...(config.query || {}),
    };
    const fetchData =
        method.toLowerCase() === 'get'
            ? {
                  params: {
                      ...commonParams,
                      ...formatParams,
                  },
              }
            : {
                  params: commonParams,
                  data: formatParams,
              };

    const startTime = Date.now();
    let requestSessionVersion = getSessionVersion();

    try {
        const headersConfig = {
            ...(axiosConfig?.headers ?? {}),
        };
        // 非线上：请求头带 x-env，供网关路由到指定测试环境
        // 未配置（null）→ 默认 offline；显式空字符串 → 不传 x-env
        if (!isProdEnv) {
            const backendSwimlane = localStore.getItem(STORAGE_KEY_BACKEND_SWIMLANE);
            if (backendSwimlane !== '') {
                headersConfig['x-env'] = backendSwimlane ?? DEFAULT_BACKEND_SWIMLANE;
            }
        }

        // 公参准备可能跨越静默换发/账号操作；最后一次检查后同步计数并发送。
        sessionWait = getSessionRequestWait(requiresAuth);
        while (sessionWait) {
            await waitForSessionTask(sessionWait, axiosConfig);
            sessionWait = getSessionRequestWait(requiresAuth);
        }
        throwIfRequestCanceled(axiosConfig);
        requestSessionVersion = getSessionVersion();
        const finishRequest = requiresAuth ? beginAuthenticatedRequest() : null;
        let originResponse: TOriginResponse<ResponseData>;
        try {
            originResponse = await axios.request<IResponse<ResponseData>>({
                method,
                url,
                ...axiosConfig,
                headers: headersConfig,
                ...fetchData,
            });
        } finally {
            // 只包裹网络过程，鉴权恢复前即释放，避免恢复等待业务在途归零。
            finishRequest?.();
        }

        const duration = Date.now() - startTime;

        // 处理成功响应
        const handlerResult = handleFetchSuccess<ResponseData>({
            originResponse,
            options: formatOptions,
            duration,
        });

        // 如果请求结果和服务端结果都成功，则返回数据
        if (handlerResult.success) {
            // 仅第一次请求成功时上报页面数据加载完成埋点
            if (!hasFirstValidRequest) {
                logPageDataLoadedTrack();
                hasFirstValidRequest = true;
            }

            if (!isNeedOriginRes && 'data' in originResponse && typeof originResponse.data === 'object') {
                const { code, message, data } = originResponse.data;
                return { code, message, data };
            }
            return originResponse;
        }

        // 否则，抛出错误，在 catch 中处理
        throw handlerResult.originResponse;
    } catch (error) {
        if (requiresAuth && !axios.isCancel(error) && isBusinessAuthError(error)) {
            // 后台恢复不替代原业务结果，也不重放订单等请求。
            recoverSessionFromAuthError(requestSessionVersion).catch(() => undefined);
        }
        const duration = Date.now() - startTime;
        // 处理完错误信息后，抛出错误，外部可通过 catch 捕获
        if (axios.isAxiosError(error) || (error !== null && typeof error === 'object' && 'data' in error)) {
            handleFetchFailed(error as TOriginResponse<ResponseData>, formatOptions, duration);
        }
        return Promise.reject(error);
    }
}

/**
 * 用于业务代码中快速定义一个Api
 *
 * TS泛型： RequestData - 请求参数的声明，如果没有请求参数，就填undefined
 * TS泛型： ResponseData - 响应中服务端传回的data信息，即 OriginResponse.data.data
 * TS泛型： Response - 响应中服务端传回的完整信息，即 OriginResponse.data，带有 code， data， message 信息
 * TS泛型： OriginResponse - 完整的响应信息，带有cookies, data, errMsg, header, statusCode 信息
 *
 * 存在 2 种重载信息：
 */
// 重载声明，当config.isNeedOriginRes = true， 会返回originResponse
export function defineApi<RequestData, ResponseData>(
    config: IGetOriginResponseConfig
): (requestData?: RequestData, otherConfig?: IRequestOptions) => Promise<TOriginResponse<ResponseData>>;

// 重载声明，当config.isNeedOriginRes = false， 会返回originResponse.data信息
export function defineApi<RequestData, ResponseData>(
    config: IGetDataResponseConfig
): (requestData?: RequestData, otherConfig?: IRequestOptions) => Promise<IResponse<ResponseData>>;

export function defineApi<RequestData, ResponseData>(config: IGetOriginResponseConfig | IGetDataResponseConfig) {
    return (
        requestData?: RequestData,
        otherConfig?: IRequestOptions
    ): Promise<IResponse<ResponseData> | TOriginResponse<ResponseData>> => {
        // 组合所有的配置
        const formatConfig: IFetchConfig<RequestData> = {
            requiresAuth: true,
            ...config,
            data: requestData,
            ...otherConfig,
        };
        return fetch(formatConfig);
    };
}
